import {useEffect, useRef} from 'react';

import {recordRumDurationVital} from '../datadogRum';
import {getPerfMetricSession} from './pageMetricSession';
import {
  type PerformanceMetricsContext,
  trackPageMetric,
} from './trackPageMetric';

/**
 * Queries that resolve faster than this are treated as Apollo cache hits
 */
const CACHE_HIT_THRESHOLD_MS = 30;

const DATA_FETCH_VITAL = 'perf_data_fetch';

/**
 * Extends PerformanceMetricsContext with an optional filter-state flag.
 * When `hasActiveFilters` is true the Segment event includes `Search: 'filtered'`
 * to distinguish search/filter fetches from default browse fetches.
 */
export type DataFetchMetricsContext = PerformanceMetricsContext & {
  hasActiveFilters?: boolean;
};

/**
 * Tracks data fetch performance by detecting loading→loaded transitions.
 *
 * For each loading→loaded cycle:
 * - Records a Datadog Duration Vital (`perf_data_fetch`) with `pageName`, `pageGroup`,
 *   and `dataType` as context attributes for RUM custom metric faceting
 * - Sends a `data_fetched` action via trackPageMetric (for RUM Explorer + Segment)
 *   including `Search: 'filtered'` when `hasActiveFilters` is true
 *
 * @param metricsContext - Must include `pageName` (fine-grained view identifier).
 *   Pass `null` to disable tracking entirely.
 * @param loading - Whether data is currently loading
 * @param dataType - Label for the data source (e.g., 'collections', 'versions')
 * @param resultCount - Number of items loaded
 * @param onReady - Optional callback fired once on first successful load
 */
export function useDataFetchTiming(
  metricsContext: DataFetchMetricsContext | null,
  loading: boolean,
  dataType: string,
  resultCount: number,
  onReady?: () => void
): void {
  const loadStartRef = useRef<number | null>(null);
  const hasFiredReady = useRef(false);
  const pageGroup = getPerfMetricSession()?.pageGroup ?? 'UnknownRoute';

  useEffect(() => {
    if (!metricsContext) {
      return;
    }

    if (loading && loadStartRef.current === null) {
      loadStartRef.current = Date.now();
    }

    if (!loading && loadStartRef.current !== null) {
      const start = loadStartRef.current;
      const duration = Date.now() - start;
      const isCacheHit = duration <= CACHE_HIT_THRESHOLD_MS;
      const {hasActiveFilters, ...metricsContextForSegment} = metricsContext;
      loadStartRef.current = null;

      recordRumDurationVital(DATA_FETCH_VITAL, {
        startTime: start,
        duration,
        description: `${metricsContext.pageName ?? pageGroup} ${dataType} fetch`,
        context: {
          pageName: metricsContext.pageName,
          pageGroup,
          dataType,
        },
      });
      trackPageMetric(metricsContextForSegment, 'data_fetched', {
        // Analytics requires a set of whitelisted words (e.g. objectType/pageNumber/duration/response)
        objectType: dataType,
        pageNumber: resultCount,
        duration: Math.round(duration),
        response: isCacheHit ? 'cache-hit' : 'cache-miss',
        ...(hasActiveFilters ? {Search: 'filtered'} : {}),
      });

      if (!hasFiredReady.current) {
        hasFiredReady.current = true;
        onReady?.();
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, dataType, resultCount, pageGroup]);
}
